import React, { useState, useEffect } from 'react';
import { Book } from '../lib/nocodb';
import { OpenLibraryService, BookSearchResult } from '../services/openLibrary';

interface BookDetailsHeaderProps {
  book: Book;
  onAddLog: () => void;
  onDeleteBook: () => void;
  onUnselectBook: () => void;
}

export function BookDetailsHeader({ book, onAddLog, onDeleteBook, onUnselectBook }: BookDetailsHeaderProps) {
  const [bookDetails, setBookDetails] = useState<BookSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookDetails = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // If we already have cover_url from database, use it
        if (book.cover_url) {
          setBookDetails({
            title: book.title,
            author: book.author,
            year: book.publication_year,
            coverUrl: book.cover_url,
            key: '',
            publisher: undefined
          });
          setIsLoading(false);
          return;
        }

        // Otherwise, search Open Library API for additional details
        const results = await OpenLibraryService.searchBooksByAuthorAndTitle(book.author, book.title);
        
        if (results.length > 0) {
          // Find the best match
          const bestMatch = results.find(result => 
            result.title.toLowerCase().includes(book.title.toLowerCase()) &&
            result.author.toLowerCase().includes(book.author.toLowerCase())
          ) || results[0];
          
          setBookDetails(bestMatch);
        } else {
          // Fallback to basic book info
          setBookDetails({
            title: book.title,
            author: book.author,
            year: book.publication_year,
            coverUrl: undefined,
            key: '',
            publisher: undefined
          });
        }
      } catch (err) {
        console.error('Error fetching book details:', err);
        setError('Failed to load book details');
        // Fallback to basic info
        setBookDetails({
          title: book.title,
          author: book.author,
          year: book.publication_year,
          coverUrl: book.cover_url,
          key: '',
          publisher: undefined
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookDetails();
  }, [book]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-24 bg-gray-200 rounded animate-pulse"></div>
          <div className="flex-1">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-start gap-6">
        {/* Book Cover */}
        <div className="flex-shrink-0">
          {bookDetails?.coverUrl ? (
            <img
              src={bookDetails.coverUrl}
              alt={`${book.title} cover`}
              className="w-20 h-30 object-cover rounded-lg border-2 border-white shadow-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-20 h-30 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg border-2 border-white shadow-lg flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          )}
        </div>

        {/* Book Information */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{book.title}</h1>
              <p className="text-lg text-gray-700 mb-1">by {book.author}</p>
              
              {/* Additional Details */}
              <div className="space-y-1 text-sm text-gray-600">
                {bookDetails?.year && (
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Published: {bookDetails.year}
                  </p>
                )}
                
                {bookDetails?.publisher && (
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Publisher: {bookDetails.publisher}
                  </p>
                )}
                
                {bookDetails?.isbn && (
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                    ISBN: {bookDetails.isbn}
                  </p>
                )}
              </div>
              
              {error && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {error}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={onUnselectBook}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors"
                title="Show all reading logs"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-blue-200">
        <div className="text-sm text-gray-600">
          Reading logs for this book
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={onAddLog}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Reading Log
          </button>
          
          <button
            onClick={onDeleteBook}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2 transition-colors"
            title="Delete this book and all its logs"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Book
          </button>
        </div>
      </div>
    </div>
  );
}